import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUsername } from '../utils/auth';
import { guides } from './definitions';

const noop = () => {};
const GuideContext = createContext({ available: false, report: noop, setValue: noop });
export const useGuide = () => useContext(GuideContext);

export function useGuideValue(name, value) {
  const { setValue } = useGuide();
  useEffect(() => { setValue(name, value); return () => setValue(name, false); }, [name, value, setValue]);
}

export function progressKey(username) { return `gymforvast:guides:v1:${encodeURIComponent(username)}`; }
function readProgress(username) {
  try { return JSON.parse(localStorage.getItem(progressKey(username))) || {}; } catch { return {}; }
}
function advance(current) {
  if (!current || current.finished) return current;
  return current.index + 1 === guides[current.id].steps.length
    ? { ...current, finished: true } : { ...current, index: current.index + 1 };
}

export function GuideProvider({ userRole, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const username = userRole ? getCurrentUsername() : '';
  const available = [1, 2, 3, 4, 5].includes(userRole) && Boolean(username);
  const [active, setActive] = useState(null);
  const [values, setValues] = useState({});
  const [completed, setCompleted] = useState({});
  const origin = useRef(null);

  useEffect(() => {
    setActive(null);
    setCompleted(readProgress(username));
  }, [username, userRole]);

  const setValue = useCallback((name, value) => setValues(previous =>
    previous[name] === value ? previous : { ...previous, [name]: value }), []);
  const report = useCallback(event => setActive(current => {
    if (!current || current.finished) return current;
    return guides[current.id].steps[current.index].event === event ? advance(current) : current;
  }), []);
  const stop = useCallback(() => {
    setActive(null);
  }, []);
  useEffect(() => {
    // Vänta tills startknappen är aktiverad igen efter avslut.
    if (!active && origin.current?.isConnected) origin.current.focus();
  }, [active]);
  const start = useCallback(id => {
    if (!available || !guides[id]) return;
    origin.current = document.activeElement;
    setActive({ id, index: 0, finished: false });
    // På den aktuella sidan bevaras även route state och pågående formulär.
    if (location.pathname !== guides[id].path) navigate(guides[id].path);
  }, [available, location.pathname, navigate]);

  useEffect(() => {
    if (!active?.finished || !available) return;
    const next = { ...readProgress(username), [active.id]: guides[active.id].version };
    setCompleted(next);
    try { localStorage.setItem(progressKey(username), JSON.stringify(next)); } catch { /* Guiden fungerar även utan lagring. */ }
  }, [active, available, username]);

  const value = useMemo(() => ({ available, active, values, completed, start, stop, report, setValue,
    next: () => setActive(advance),
    previous: () => setActive(current => current ? { ...current, index: Math.max(0, current.index - 1), finished: false } : current)
  }), [available, active, values, completed, start, stop, report, setValue]);
  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}

export function GuideButton({ id }) {
  const { available, active, start, completed } = useGuide();
  if (!available) return null;
  return <button type="button" className="theme-button-secondary my-2 px-4 py-2 text-sm"
    disabled={Boolean(active)} onClick={() => start(id)}>
    {completed[id] === guides[id].version ? 'Visa igen' : 'Visa mig hur'}: {guides[id].title}
  </button>;
}

export function GuidePanel() {
  const { available, active, values, next, previous, stop } = useGuide();
  const { pathname } = useLocation();
  const heading = useRef(null);
  const [found, setFound] = useState(false);
  const [compact, setCompact] = useState(false);
  const guide = active && guides[active.id];
  const step = guide?.steps[active.index];
  const onPage = Boolean(guide && (pathname === guide.path || (active.id === 'overview' && pathname === '/') ||
    (active.id === 'create' && active.index === 2 && pathname === '/videos')));
  const target = pathname === '/videos' && step?.videoTarget ? step.videoTarget : step?.target;

  useEffect(() => {
    if (!active || !available) return undefined;
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); stop(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, available, stop]);

  useEffect(() => {
    setCompact(false);
    heading.current?.focus({ preventScroll: true });
  }, [active?.id, active?.index, active?.finished]);

  useEffect(() => {
    setFound(false);
    if (!active || active.finished || !available || !onPage) return undefined;
    let marked;
    const find = () => {
      const element = document.querySelector(`[data-guide="${target}"]`);
      if (element === marked) return;
      marked?.classList.remove('guide-highlight');
      marked = element;
      setFound(Boolean(element));
      if (element) {
        element.classList.add('guide-highlight');
        element.scrollIntoView?.({ block: 'center', behavior: 'auto' });
      }
    };
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); marked?.classList.remove('guide-highlight'); };
  }, [active, available, onPage, target]);

  if (!active || !available) return null;
  const ready = onPage && found && (!step.ready || Boolean(values[step.ready]));
  const manualNext = !step.event || (step.ready && Boolean(values[step.ready]));
  return <aside className="guide-panel" aria-label="Interaktiv guide">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 ref={heading} tabIndex={-1} aria-describedby="guide-instruction" className="font-bold outline-none" aria-live="polite">
        {guide.title} · {active.finished ? 'Klar' : `${active.index + 1} av ${guide.steps.length}`}
      </h2>
      <div className="flex gap-3">
        <button type="button" onClick={() => setCompact(value => !value)} aria-expanded={!compact} className="underline">{compact ? 'Visa instruktion' : 'Fäll ihop'}</button>
        <button type="button" onClick={stop} className="underline">Avsluta guide</button>
      </div>
    </div>
    {!compact && <>
      <p className="mt-2 font-semibold">{active.finished ? 'Bra, du har gått igenom guiden.' : step.title}</p>
      <p id="guide-instruction" className="mt-1 text-sm">{active.finished ? 'Du kan starta guiden igen från hjälpen eller sidans guideknapp.' : step.text}</p>
      {!active.finished && !onPage && <p role="status" className="mt-2 text-sm">Guiden är pausad på den här sidan. Gå tillbaka i appen till guidens sida eller avsluta guiden. Dina ändringar behålls.</p>}
      {!active.finished && onPage && !found && <p role="status" className="mt-2 text-sm">Väntar på innehållet. Kontrollera sidans laddning, sökning eller felmeddelande. Du kan alltid avsluta guiden.</p>}
      <div className="mt-3 flex gap-3">
        {!active.finished && <button type="button" className="theme-button-secondary px-4 py-2" disabled={active.index === 0 || !onPage} onClick={previous}>Tillbaka</button>}
        {active.finished ? <button type="button" className="theme-button-primary px-4 py-2" onClick={stop}>Klart</button> :
          <button type="button" className="theme-button-primary px-4 py-2" disabled={!ready || !manualNext} onClick={next}>
            {step.event && !manualNext ? 'Utför steget i appen' : active.index === guide.steps.length - 1 ? 'Slutför guide' : 'Nästa'}
          </button>}
      </div>
    </>}
  </aside>;
}
